import { api } from '../../config/api';
import {
  ProductionJob,
  ProductionStandard,
  ScheduleEntry,
  VarianceEntry,
  ProductionActual,
  ImportResult,
  PressConfig,
  DowntimeWindow,
  ResolvedJobSeparations,
  SeparationProfile,
  SeparationProfileSummary,
  SeparationProfileSource,
  SeparationScopeType,
  SeparationUnit,
  SeparationUnitKind,
} from './productionTypes';
import type { InkForecast } from './inkForecast/inkForecastModel';

// ----- Jobs -----

export interface JobListParams {
  search?: string;
  customer?: string;
  unscheduled?: boolean;
  limit?: number;
}

export async function listJobs(params: JobListParams = {}): Promise<ProductionJob[]> {
  const response = await api.get('/production/jobs', {
    params: {
      search: params.search || undefined,
      customer: params.customer || undefined,
      unscheduled: params.unscheduled ? 'true' : undefined,
      limit: params.limit || undefined,
    },
  });
  return response.data.jobs;
}

export async function importJobsCsv(file: File): Promise<ImportResult> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post('/production/jobs/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
}

export interface DeleteAllJobsResult {
  success: boolean;
  jobs_deleted: number;
  schedule_deleted: number;
  separations_deleted: number;
  thumbnails_deleted: number;
}

export async function deleteAllJobs(): Promise<DeleteAllJobsResult> {
  const response = await api.delete('/production/jobs/all');
  return response.data;
}

// ----- Ready-to-Run manual regrouping (job splits) -----

/**
 * Assign a fresh split_key to the given jobs so they form their own card. Jobs
 * already on a press as part of a family run are split into a NEW family run
 * (the explicit split event); its ids come back in family_run_ids.
 */
export async function splitJobs(
  jobNumbers: string[],
): Promise<{ split_key: string; count: number; family_run_ids: number[] }> {
  const response = await api.post('/production/job-splits', { job_numbers: jobNumbers });
  return {
    split_key: response.data.split_key,
    count: response.data.count,
    family_run_ids: response.data.family_run_ids ?? [],
  };
}

/**
 * Rejoin: clear a split group (by key) so its jobs fall back to the default
 * group. A scheduled family run among them is merged back into the run it was
 * split from; `merged` maps old id → surviving id.
 */
export async function rejoinSplit(
  splitKey: string,
): Promise<{ deleted: number; merged: Record<string, number> }> {
  const response = await api.delete('/production/job-splits', { data: { split_key: splitKey } });
  return { deleted: response.data.deleted, merged: response.data.merged ?? {} };
}

/** Rejoin by job — the board's "Rejoin group" on a scheduled bar. */
export async function rejoinJobs(
  jobNumbers: string[],
): Promise<{ deleted: number; merged: Record<string, number> }> {
  const response = await api.delete('/production/job-splits', { data: { job_numbers: jobNumbers } });
  return { deleted: response.data.deleted, merged: response.data.merged ?? {} };
}

export interface SeedTestJobsResult {
  success: boolean;
  inserted: number;
  updated: number;
  total: number;
}

export async function seedTestJobs(): Promise<SeedTestJobsResult> {
  const response = await api.post('/production/jobs/seed-test');
  return response.data;
}

// ----- Prism sync status -----

/** Mirrors the production.sync_state row for sync_type='prism_jobs'. */
export interface SyncStatus {
  sync_type: string;
  last_success_at: string | null;
  last_error_at: string | null;
  last_error_msg: string | null;
  total_syncs: number;
  error_count: number;
  stale: boolean;
  interval_seconds: number;
}

/** Last time the Prism→production job sync succeeded (drives the dock freshness chip). */
export async function getSyncStatus(): Promise<SyncStatus> {
  const response = await api.get('/production/sync-status');
  return response.data.data;
}

// ----- Standards -----

export async function listStandards(pressCode?: string): Promise<ProductionStandard[]> {
  // Omit the `press` param to fetch standards for ALL presses (the backend
  // only filters when a press is supplied). Pass a code to scope to one press.
  const response = await api.get('/production/standards', {
    params: pressCode ? { press: pressCode } : {},
  });
  return response.data.standards;
}

export interface StandardUpsert {
  press_code: string;
  scope_type: ProductionStandard['scope_type'];
  scope_value: string | null;
  setup_minutes: number;
  run_rate_per_hour: number;
  washup_minutes: number;
  notes?: string | null;
}

export async function upsertStandard(payload: StandardUpsert): Promise<ProductionStandard> {
  const response = await api.post('/production/standards', payload);
  return response.data.standard;
}

export async function updateStandard(
  id: number,
  patch: Partial<Pick<ProductionStandard, 'setup_minutes' | 'run_rate_per_hour' | 'washup_minutes' | 'notes'>>
): Promise<ProductionStandard> {
  const response = await api.patch(`/production/standards/${id}`, patch);
  return response.data.standard;
}

export async function deleteStandard(id: number): Promise<void> {
  await api.delete(`/production/standards/${id}`);
}

export interface ResolveStandardParams {
  press: string;
  stock?: string | null;
  material?: string | null;
  forme?: string | null;
  customer?: string | null;
}

/** The standard that applies to a job — the same cascade scheduling uses. */
export interface ResolvedStandard {
  id: number;
  press_code: string;
  scope_type: ProductionStandard['scope_type'];
  scope_value: string | null;
  setup_minutes: number;
  run_rate_per_hour: number;
  washup_minutes: number;
}

/** Resolve which standard governs a job's speed (null if none, not even default). */
export async function resolveStandard(params: ResolveStandardParams): Promise<ResolvedStandard | null> {
  const response = await api.get('/production/standards/resolve', {
    params: {
      press: params.press,
      stock: params.stock || undefined,
      material: params.material || undefined,
      forme: params.forme || undefined,
      customer: params.customer || undefined,
    },
  });
  return response.data.standard;
}

// ----- Customer colours (Press Schedule Board) -----

/** A per-customer ticket colour row from production.customer_colours. */
export interface CustomerColour {
  customer_code: string;
  fill: string;
  text: string;
  border: string | null;
  updated_at: string;
}

export async function listCustomerColours(): Promise<CustomerColour[]> {
  const response = await api.get('/production/customer-colours');
  return response.data.colours;
}

export interface CustomerColourUpsert {
  customer_code: string;
  fill: string;
  text: string;
  border?: string | null;
}

export async function upsertCustomerColour(payload: CustomerColourUpsert): Promise<CustomerColour> {
  const response = await api.post('/production/customer-colours', payload);
  return response.data.colour;
}

/** Remove a customer's colour override — it reverts to the forme-hash fallback. */
export async function deleteCustomerColour(customerCode: string): Promise<void> {
  await api.delete(`/production/customer-colours/${encodeURIComponent(customerCode)}`);
}

// ----- Schedule -----

export interface ScheduleListParams {
  press?: string;
  from?: string;
  to?: string;
}

export async function listSchedule(params: ScheduleListParams = {}): Promise<ScheduleEntry[]> {
  const response = await api.get('/production/schedule', {
    params: {
      press: params.press || 'P15',
      from: params.from || undefined,
      to: params.to || undefined,
    },
  });
  return response.data.schedule;
}

/**
 * Every press in one call — real lanes plus the OTHER / ELSEWHERE holding
 * codes. Rows carry press_code; the board groups them client-side. Replaces
 * the six per-press requests the board used to fire on mount, on every save
 * and on every poll tick.
 */
export async function listScheduleAll(): Promise<ScheduleEntry[]> {
  const response = await api.get('/production/schedule', { params: { press: 'all' } });
  return response.data.schedule;
}

export interface ScheduleCreate {
  job_number: string;
  press_code: string;
  planned_start: string;
  notes?: string | null;
  /**
   * Which press pass this run is. Omit (or 1) for ordinary single-sided work.
   * The server rejects a pass the job doesn't have, and a pass already placed
   * on ANY press — the two passes are independent, but neither runs twice.
   */
  pass_no?: number;
  /**
   * Join an existing family run (migration 141). The board schedules a family
   * one job at a time: the first POST omits this and the server mints a run
   * when the row lands on a press lane; the rest pass that id back to join it.
   */
  family_run_id?: number | null;
  /**
   * When true the new entry takes sequence=1 (existing entries on this
   * press get bumped by 1) and the supplied planned_start is honoured as
   * the new anchor. Multi-job batches that want this behaviour should POST
   * the jobs in REVERSE order so the LAST POST lands at sequence=1.
   */
  at_anchor?: boolean;
  /**
   * 1-based position in the press queue to insert at; everything from that slot
   * onward is bumped one later. Takes precedence over at_anchor. Clamped
   * server-side to the queue length and to sit after any running/completed work.
   * This is what the board's gap-opening drag sends — without it, the only
   * choices are tail-append or jump-to-front.
   */
  at_sequence?: number;
}

export async function addScheduleEntry(payload: ScheduleCreate): Promise<ScheduleEntry> {
  const response = await api.post('/production/schedule', payload);
  return response.data.entry;
}

export interface ScheduleUpdate {
  /** Relocate the entry to a different press. Both presses re-cascade. */
  press_code?: string;
  planned_start?: string;
  sequence?: number;
  planned_setup_min?: number;
  planned_run_min?: number;
  planned_washup_min?: number;
  status?: ScheduleEntry['status'];
  notes?: string | null;
  needs_remakeready_after_pause?: boolean;
  run_through_downtime?: boolean;
}

export async function updateScheduleEntry(id: number, patch: ScheduleUpdate): Promise<ScheduleEntry> {
  const response = await api.patch(`/production/schedule/${id}`, patch);
  return response.data.entry;
}

export async function deleteScheduleEntry(id: number): Promise<void> {
  await api.delete(`/production/schedule/${id}`);
}

/**
 * "Rebase to reality" — commit the live projection into the committed plan for
 * a press. Only not-started (planned) entries move; completed/in-progress rows
 * are untouched. Returns how many entries were rebased.
 */
export async function rebaseSchedule(pressCode: string): Promise<{ rebased: number }> {
  const response = await api.post('/production/schedule/rebase', { press_code: pressCode });
  return { rebased: response.data.rebased };
}

/**
 * Admin / testing clean-up: delete every not-started (planned) job on a press
 * regardless of planned_start. Running and completed jobs are left untouched;
 * the cleared jobs return to Ready-to-Run. Returns how many were deleted.
 */
export async function clearFutureSchedule(pressCode: string): Promise<{ deleted: number }> {
  const response = await api.delete('/production/schedule/clear-future', {
    params: { press: pressCode },
  });
  return { deleted: response.data.deleted };
}

export async function listVariance(pressCode = 'P15'): Promise<VarianceEntry[]> {
  const response = await api.get('/production/schedule/variance', {
    params: { press: pressCode },
  });
  return response.data.variance;
}

// ----- Actuals -----

export interface ActualUpsert {
  schedule_id: number;
  actual_start?: string | null;
  actual_end?: string | null;
  actual_setup_min?: number | null;
  actual_run_min?: number | null;
  actual_washup_min?: number | null;
  quantity_good?: number | null;
  quantity_waste?: number | null;
  notes?: string | null;
  entered_by?: string | null;
}

export async function upsertActual(payload: ActualUpsert): Promise<ProductionActual> {
  const response = await api.post('/production/actuals', payload);
  return response.data.actual;
}

// ----- Downtime windows -----

export async function listDowntimeWindows(pressCode?: string): Promise<DowntimeWindow[]> {
  const response = await api.get('/production/downtime-windows', {
    params: pressCode ? { press: pressCode } : undefined,
  });
  return response.data.downtime_windows;
}

export interface DowntimeWindowUpsert {
  press_code: string;
  kind: DowntimeWindow['kind'];
  effect?: DowntimeWindow['effect'];
  dow_start?: number | null;
  tod_start?: string | null;
  dow_end?: number | null;
  tod_end?: string | null;
  starts_at?: string | null;
  ends_at?: string | null;
  label?: string | null;
  notes?: string | null;
}

export async function createDowntimeWindow(
  payload: DowntimeWindowUpsert,
): Promise<DowntimeWindow> {
  const response = await api.post('/production/downtime-windows', payload);
  return response.data.downtime_window;
}

export async function updateDowntimeWindow(
  id: number,
  patch: Partial<DowntimeWindowUpsert>,
): Promise<DowntimeWindow> {
  const response = await api.patch(`/production/downtime-windows/${id}`, patch);
  return response.data.downtime_window;
}

export async function deleteDowntimeWindow(id: number): Promise<void> {
  await api.delete(`/production/downtime-windows/${id}`);
}

// ----- Press config -----

export async function listPressConfig(): Promise<PressConfig[]> {
  const response = await api.get('/production/press-config');
  return response.data.press_config;
}

export async function getPressConfig(pressCode: string): Promise<PressConfig> {
  const response = await api.get(`/production/press-config/${pressCode}`);
  return response.data.press_config;
}

// ----- Separation profiles -----

export interface SeparationProfileListParams {
  press?: string;
  scope?: SeparationScopeType;
  unapproved?: boolean;
  search?: string;
}

export async function listSeparationProfiles(
  params: SeparationProfileListParams = {},
): Promise<SeparationProfileSummary[]> {
  const response = await api.get('/production/separation-profiles', {
    params: {
      press: params.press || undefined,
      scope: params.scope || undefined,
      unapproved: params.unapproved ? 'true' : undefined,
      search: params.search || undefined,
    },
  });
  return response.data.profiles;
}

export async function getSeparationProfile(id: number): Promise<SeparationProfile> {
  const response = await api.get(`/production/separation-profiles/${id}`);
  return response.data.profile;
}

export interface SeparationProfileUpsert {
  press_code: string;
  scope_type: SeparationScopeType;
  scope_value: string | null;
  source?: SeparationProfileSource;
  source_pdf_path?: string | null;
  approved?: boolean;
  notes?: string | null;
}

export async function upsertSeparationProfile(
  payload: SeparationProfileUpsert,
): Promise<SeparationProfile> {
  const response = await api.post('/production/separation-profiles', payload);
  return response.data.profile;
}

export interface SeparationProfilePatch {
  approved?: boolean;
  notes?: string | null;
  source?: SeparationProfileSource;
  source_pdf_path?: string | null;
}

export async function patchSeparationProfile(
  id: number,
  patch: SeparationProfilePatch,
): Promise<SeparationProfile> {
  const response = await api.patch(`/production/separation-profiles/${id}`, patch);
  return response.data.profile;
}

export async function deleteSeparationProfile(id: number): Promise<void> {
  await api.delete(`/production/separation-profiles/${id}`);
}

export type SeparationUnitUpsert = SeparationUnit;

export async function upsertSeparationUnit(
  profileId: number,
  unit: SeparationUnitUpsert,
): Promise<SeparationProfile> {
  const response = await api.post(`/production/separation-profiles/${profileId}/units`, unit);
  return response.data.profile;
}

export async function deleteSeparationUnit(
  profileId: number,
  kind: SeparationUnitKind,
  index: number,
): Promise<SeparationProfile> {
  const response = await api.delete(
    `/production/separation-profiles/${profileId}/units/${kind}/${index}`,
  );
  return response.data.profile;
}

export interface ResolveSeparationKey {
  customer_code?: string | null;
  customer_ref?: string | null;
  stock_code?: string | null;
  material_desc?: string | null;
}

export async function resolveSeparation(
  press: string,
  key: ResolveSeparationKey,
): Promise<SeparationProfile | null> {
  const response = await api.get('/production/separation-profiles/resolve', {
    params: {
      press,
      customer: key.customer_code || undefined,
      customer_ref: key.customer_ref || undefined,
      stock_code: key.stock_code || undefined,
      material_desc: key.material_desc || undefined,
    },
  });
  return response.data.profile ?? null;
}

/**
 * Resolve N keys in one POST round-trip. The response is a same-length,
 * same-order array — caller is responsible for keying it back to its
 * inputs (typically by composing a stable cache key from the input row).
 */
export async function resolveSeparationsBulk(
  press: string,
  keys: ResolveSeparationKey[],
): Promise<(SeparationProfile | null)[]> {
  if (keys.length === 0) return [];
  const response = await api.post('/production/separation-profiles/resolve-bulk', {
    press,
    keys,
  });
  return response.data.resolutions ?? [];
}

export interface SkuSequencePayload {
  press_code: string;
  customer_code: string;
  customer_ref: string;
  approved_by?: string | null;
  stock_code?: string | null;
  material_desc?: string | null;
  notes?: string | null;
  // Omit `units` to snapshot the current cascade resolution (the
  // "Approve current sequence" tick path). Pass it explicitly when
  // saving custom edits from the SKU drawer.
  units?: SeparationUnit[];
}

/**
 * Single-shot endpoint that creates or updates an artwork-scope
 * SeparationProfile for (customer_code, customer_ref) with approved=true.
 * Backend handles unit replacement atomically and stamps the approval
 * audit fields. Returns the saved profile.
 */
export async function saveSkuSequence(
  payload: SkuSequencePayload,
): Promise<SeparationProfile> {
  const response = await api.post('/production/separation-profiles/sku-sequence', payload);
  return response.data.profile;
}

// ----- Cloudflow extraction -----

export interface ExtractSeparationsRequest {
  press_code: string;
  pdf_path: string;
  customer_code: string;
  customer_ref: string;
  notes?: string | null;
}

export interface ExtractSeparationsResponse {
  profile: SeparationProfile;
  raw_separations: {
    name: string;
    kind: string;
    hex_colour: string | null;
    pdf_index: number;
    cloudflow_type: string;
  }[];
}

export async function extractSeparationsFromPdf(
  payload: ExtractSeparationsRequest,
): Promise<ExtractSeparationsResponse> {
  const response = await api.post('/production/separations/extract', payload);
  return {
    profile: response.data.profile,
    raw_separations: response.data.raw_separations,
  };
}

/**
 * Resolve the filled separation grid for one job: backend pulls the artwork
 * colorants (cache → Cloudflow on miss), then applies the cascade-resolved
 * profile so role-only template slots show the actual sep at render time.
 */
export async function getJobSeparations(
  jobNumber: string,
  press = 'P15',
  /**
   * Set false to skip resolving the separation profile — one fewer query per
   * call. Only safe when the caller reads none of `profile`, `resolved_units`
   * or `leftover_separations`; the board is the case this exists for, since it
   * makes ~67 of these per load and uses none of them. TodayView and
   * SkuSequenceDrawer DO read them, so the default stays true.
   */
  includeProfile = true,
): Promise<ResolvedJobSeparations> {
  const response = await api.get(`/production/jobs/${encodeURIComponent(jobNumber)}/separations`, {
    params: includeProfile ? { press } : { press, profile: 'false' },
  });
  return response.data;
}

export interface BulkJobSeparations {
  success: boolean;
  press: string;
  requested: number;
  /** Keyed by job_number; each entry is the single-endpoint shape. */
  results: Record<string, ResolvedJobSeparations>;
  /** Jobs with no cached artwork — resolve these via getJobSeparations. */
  pending: string[];
}

/**
 * Resolve MANY jobs' separations in one round trip — cache-only on the server
 * (never touches Cloudflow), so uncached jobs come back in `pending` for the
 * caller to trickle through the single endpoint. profile is always skipped:
 * this exists for the board, which reads none of the profile fields.
 * Measured in production 2026-09-03: ~200 ms server-side for a 62-job batch.
 */
export async function getJobSeparationsBulk(
  jobNumbers: string[],
  press = 'P15',
  signal?: AbortSignal,
): Promise<BulkJobSeparations> {
  const response = await api.post(
    '/production/jobs/separations/bulk',
    { press, job_numbers: jobNumbers, profile: false },
    { signal },
  );
  return response.data;
}

// ----- Ink demand -----

export interface InkDemandRow {
  name: string;
  kind: string;
  hex_colour: string | null;
  covered_area_mm2: number | null;
  coverage_frac: number | null;
  demand_kg: number | null;
}

export interface InkDemandInputs {
  run_length_sheets: number | null;
  sheet_width_mm: number | null;
  sheet_height_mm: number | null;
  sheet_area_m2: number | null;
  trim_area_mm2: number | null;
  mileage_g_per_m2: number;
  kg_per_sheet_at_solid: number | null;
}

export interface InkDemandReport {
  job_number: string;
  customer_code: string | null;
  customer_ref: string | null;
  material_desc: string | null;
  pdf_path: string | null;
  artwork_status: 'no_path' | 'resolved' | 'not_found' | 'error';
  cache_hit: boolean;
  cloudflow_error: string | null;
  computable: boolean;
  inputs: InkDemandInputs;
  separations: InkDemandRow[];
  skipped_separations: { name: string; kind: string; reason: string }[];
  total_demand_kg: number | null;
  warnings: string[];
}

/**
 * Per-separation ink demand (kg) for one job. Backend reads the run length +
 * sheet size, pulls the artwork's per-separation coverage from Cloudflow, and
 * applies the mileage model (skipping coatings + technical layers). `mileage`
 * overrides the default ink film weight (g/m² at 100% solid).
 */
export async function getJobInkDemand(
  jobNumber: string,
  mileage?: number,
): Promise<InkDemandReport> {
  const response = await api.get(`/production/jobs/${encodeURIComponent(jobNumber)}/ink-demand`, {
    params: { mileage: mileage ?? undefined },
  });
  return response.data;
}

// ----- ECG vs PMS ink-cost comparison -----

/** One side of the comparison, as sent. `prices` is keyed on the separation
 *  name exactly as the artwork spells it; the backend case-folds. */
export interface InkComparisonVariantInput {
  label: string;
  pdf_path: string;
  prices?: Record<string, number>;
  mileage_by_name?: Record<string, number>;
}

export interface InkComparisonRequest {
  sheets: number;
  sheet_width_mm: number;
  sheet_height_mm: number;
  /** Optional flat g/m² across BOTH sides. Omit to let each colour keep its own
   *  class default (ECG/process 1.2, spots 1.5) — which is the difference this
   *  screen exists to price, so overriding it flattens the answer. */
  mileage?: number;
  variants: InkComparisonVariantInput[];
}

export interface InkCostRow {
  name: string;
  kind: string;
  hex_colour: string | null;
  covered_area_mm2: number | null;
  coverage_frac: number | null;
  mileage_g_per_m2: number;
  mileage_overridden: boolean;
  demand_kg: number | null;
  price_per_kg: number | null;
  cost: number | null;
}

export interface InkComparisonVariant {
  label: string;
  pdf_path: string | null;
  artwork_status: 'no_path' | 'resolved' | 'not_found' | 'error';
  cache_hit: boolean;
  cloudflow_error: string | null;
  computable: boolean;
  inputs: InkDemandInputs;
  separations: InkCostRow[];
  skipped_separations: { name: string; kind: string; reason: string }[];
  total_demand_kg: number | null;
  total_cost: number | null;
  priced_count: number;
  /** Separations still needing a price — until this is empty the money verdict
   *  is withheld (`comparison.comparable` stays false). */
  unpriced: string[];
  fully_priced: boolean;
  warnings: string[];
}

export interface InkComparisonResult {
  inputs: {
    sheets: number;
    sheet_width_mm: number;
    sheet_height_mm: number;
    sheet_area_m2: number;
    mileage_g_per_m2: number | null;
  };
  variants: InkComparisonVariant[];
  comparison: {
    comparable: boolean;
    cheaper_label: string | null;
    /** The saving, as a positive number off the dearer side. */
    cost_delta: number | null;
    cost_delta_pct: number | null;
    /** Second variant minus first. Available whenever both sides computed,
     *  priced or not — weight needs no commercial input. */
    kg_delta: number | null;
    notes: string[];
  };
}

/**
 * Price the same carton printed two ways: an ECG build vs a spot/PMS build.
 *
 * Ink mileage ONLY — no wash-up waste, minimum mix quantity or press-pass cost.
 * All three of those favour ECG, so a verdict here is a lower bound on its case.
 */
export async function compareInkCost(payload: InkComparisonRequest): Promise<InkComparisonResult> {
  const response = await api.post('/production/ink-comparison', payload);
  return response.data;
}

// ----- Ink demand summary (ordering roll-up across all open jobs) -----

/** One job's share of a single ink's demand. */
export interface InkDemandJob {
  job_number: string;
  customer_code: string | null;
  customer_ref: string | null;
  demand_kg: number;           // at the mileage the report was fetched at
  /** Run length. Stamped onto a shortfall PO's notes so a later volume change
   *  is visible against what was actually ordered. */
  run_length_sheets: number | null;
}

export interface InkDemandSummaryRow {
  ink_key: string | null;      // resolved ink product key; null = not yet assigned
  name: string;                // ink description (assigned) or separation name (unassigned)
  series: string | null;       // manufacturer · series · system
  series_suffix: string | null;   // null when unassigned — the grouping key
  manufacturer: string | null;
  series_name: string | null;
  ink_system: string | null;      // UV | AQ | CO — never sum across these
  kind: string | null;
  hex_colour: string | null;
  series_hex: string | null;
  low_migration: boolean;
  assigned: boolean;
  /** GROSS requirement — what the open jobs need, before anything is kitted. */
  total_kg: number;
  /** Already on an OPEN trolley run for it (sealed + opened tubs). */
  allocated_kg?: number;
  /** total_kg − allocated_kg, floored at 0 — what is still to be covered. */
  outstanding_kg?: number;
  job_count: number;
  /** WHICH jobs need this ink, heaviest first — not just how many. */
  jobs: InkDemandJob[];
}

export interface InkDemandExcludedJob {
  job_number: string;
  customer_code: string | null;
  reason: string;
}

export interface InkDemandPendingJob {
  job_number: string;
  customer_code: string | null;
}

export interface InkDemandSummary {
  mileage_g_per_m2: number;
  inks: InkDemandSummaryRow[];
  grand_total_kg: number;
  /** Sum of outstanding_kg — the gross total net of everything already kitted. */
  grand_outstanding_kg?: number;
  included_count: number;
  excluded: InkDemandExcludedJob[];
  pending_extraction: InkDemandPendingJob[];
  /**
   * Contributing jobs that print BOTH SIDES. Their second pass contributes
   * nothing to these totals: the reverse side's artwork is not recorded
   * anywhere in the business, so there are no back-side separations to cost,
   * and counting the front twice would order 2x a PMS we won't use and none of
   * the back's. Surfaced so the shortfall is stated rather than hidden inside
   * the number.
   */
  two_pass_count?: number;
}

/**
 * Ink-demand roll-up across every open job, grouped by ink name — the ordering
 * report. CACHE-ONLY on the backend: it never pulls Cloudflow, so jobs whose
 * artwork isn't cached come back in `pending_extraction` for the caller to warm
 * one-at-a-time (via getJobInkDemand, which populates the cache).
 *
 * Demand is linear in mileage, so callers fetch once at mileage = 1 and scale
 * kg client-side — the mileage knob then never triggers a refetch.
 */
export async function getInkDemandSummary(mileage?: number): Promise<InkDemandSummary> {
  const response = await api.get('/production/ink-demand/summary', {
    params: { mileage: mileage ?? undefined },
  });
  return response.data;
}

/**
 * The same roll-up narrowed to ONE gang run — i.e. "what ink does this job group
 * need?", which is the suggested kit for a trolley.
 *
 * Rows with `assigned: false` are the important ones: the artwork needs that
 * separation and we know the kg, but nobody has chosen WHICH ink product covers
 * it (Ink Assign). Those can't be kitted — you cannot draw "Cyan" off a shelf,
 * only "Huber Cyan Eco CO" — so surface them, never silently drop them.
 */
export async function getInkDemandForJobs(
  jobNumbers: string[],
  mileage?: number,
): Promise<InkDemandSummary> {
  const response = await api.get('/production/ink-demand/summary', {
    params: { jobs: jobNumbers.join(','), mileage: mileage ?? undefined },
  });
  return response.data;
}

// ----- Job groups (the Planning Board's Ready-to-Run tiles) -----

/**
 * A job group = the board's forme group = a gang run. Keyed
 * customer_code|forme_number|split_key, exactly as board/useGanttData.ts groups
 * them, but resolved server-side so a trolley can be kitted from one.
 *
 * A SCHEDULED group carries `family_run_id` — the scheduler's immutable id for
 * the gang run (migration 141); its `group_key` is then "fr:<id>". A backlog
 * group's key is still DERIVED and shifts if a job's forme or split changes.
 * Never persist the key; a trolley run records `family_run_id` when there is
 * one and always the resolved `job_numbers`, which stay true regardless.
 */
export interface JobGroup {
  group_key: string;
  family_run_id: number | null;
  family_state: string | null;
  customer_code: string;
  customer_name: string;
  forme_number: string | null;
  /** Trim — what tells a forme-less (label) group apart. */
  length: number | null;
  width: number | null;
  stock_code: string | null;
  coating: string | null;
  split_key: string | null;
  job_numbers: string[];
  job_count: number;
  earliest_due: string | null;
  total_sheets: number;
  press_code: string | null;              // null = still in the backlog
  earliest_planned_start: string | null;
  scheduled_count: number;
  /** False when the planner has committed only part of the forme — kitting ink
   *  for the whole group would then over-draw. */
  fully_scheduled: boolean;
  jobs: {
    job_number: string;
    customer_ref: string | null;
    description: string | null;
    due_date: string | null;
    quantity: number | null;
    run_length_sheets: number | null;
    press_code: string | null;
    sequence: number | null;
  }[];
}

/**
 * "forme 1234" — or, for label work with no forme, "trim 101×244" — then the
 * stock code and the coating when there is one. Groups split on both, so two
 * groups can share a forme/trim and differ only by paper (FMA 63×160 on SP080
 * vs UNISUP080) or only by coating (UBA's Whiskas vs My Dog on 101×244).
 */
export function jobGroupLabel(g: JobGroup): string {
  const struct = g.forme_number
    ? `forme ${g.forme_number}`
    : g.length != null && g.width != null
      ? `trim ${Number(g.length)}×${Number(g.width)}`
      : 'forme —';
  const parts = [struct];
  if (g.stock_code) parts.push(g.stock_code);
  if (g.coating && g.coating !== 'NONE') parts.push(g.coating);
  return parts.join(' · ');
}

export async function getJobGroups(params: {
  press?: string; scheduled?: boolean; limit?: number;
} = {}): Promise<JobGroup[]> {
  const response = await api.get('/production/job-groups', {
    params: {
      press: params.press || undefined,
      scheduled: params.scheduled === undefined ? undefined : (params.scheduled ? 1 : 0),
      limit: params.limit || undefined,
    },
  });
  return response.data.groups ?? [];
}

export interface ArtworkThumbnail {
  data_url: string | null;
  cache_hit: boolean;
  error: string | null;
}

/**
 * Fetch a Cloudflow PDF thumbnail for a job's artwork. Backend caches by
 * (pdf_path, width). Throws on Cloudflow errors so React Query treats the
 * query as failed (rather than caching the error response forever under
 * staleTime: Infinity).
 */
export async function getArtworkThumbnail(
  jobNumber: string,
  width = 300,
): Promise<ArtworkThumbnail> {
  const response = await api.get(
    `/production/jobs/${encodeURIComponent(jobNumber)}/artwork-thumbnail`,
    { params: { width } },
  );
  const payload = response.data as ArtworkThumbnail;
  if (payload.error) {
    throw new Error(payload.error);
  }
  return payload;
}

/**
 * Fetch a thumbnail straight from the item code (customer_code + customer_ref) —
 * no job needed. Used by Ink Assign, which sources SKUs from the category list.
 */
export async function getArtworkThumbnailByRef(
  customer: string,
  ref: string,
  width = 300,
): Promise<ArtworkThumbnail> {
  const response = await api.get('/production/artwork-thumbnail', {
    params: { customer, ref, width },
  });
  const payload = response.data as ArtworkThumbnail;
  if (payload.error) {
    throw new Error(payload.error);
  }
  return payload;
}

/**
 * Fetch a thumbnail for an EXPLICIT Cloudflow path — for artwork identified by
 * where it sits rather than by an item code, i.e. the ECG-vs-PMS comparison,
 * where one of the two files may not follow the naming convention or belong to
 * any job. The backend percent-encodes the path before calling Cloudflow, so a
 * pasted path with literal spaces is fine here.
 */
export async function getArtworkThumbnailByPath(
  path: string,
  width = 300,
): Promise<ArtworkThumbnail> {
  const response = await api.get('/production/artwork-thumbnail', {
    params: { path, width },
  });
  const payload = response.data as ArtworkThumbnail;
  if (payload.error) {
    throw new Error(payload.error);
  }
  return payload;
}

export type WarmStatus = 'cached' | 'extracted' | 'no_artwork' | 'no_path';

/**
 * Warm ONE SKU's separations from Cloudflow by item code (customer + ref). The
 * backend is cache-first (positive + negative), so this only hits Cloudflow for
 * a genuinely-new SKU; misses are recorded so they aren't re-asked. Always drive
 * this through the shared serial() queue so Cloudflow sees one call at a time.
 */
export async function warmArtworkSeparations(customer: string, ref: string): Promise<WarmStatus> {
  const response = await api.get('/production/artwork-separations', { params: { customer, ref } });
  return (response.data?.status ?? 'no_path') as WarmStatus;
}

/** One level of the Cloudflow file store — folders + PDFs, for the artwork picker. */
export interface ArtworkBrowseEntry {
  path: string;
  name: string;
}

export interface ArtworkBrowseResult {
  folder: string;
  name: string;
  /** null at the browse root (cloudflow://Production) — nothing above it is offered. */
  parent: string | null;
  folders: ArtworkBrowseEntry[];
  files: ArtworkBrowseEntry[];
  /** Non-PDF files in this folder, counted but not listed. */
  hidden_files: number;
  error?: string | null;
}

/**
 * List one Cloudflow folder. Deliberately NOT routed through serial(): this is a
 * cheap listing call a human is waiting on, and queueing it behind a warm run of
 * a few hundred SKUs would make the picker feel broken.
 */
export async function browseArtworkFolder(folder?: string): Promise<ArtworkBrowseResult> {
  const response = await api.get('/production/artwork-browse', {
    params: folder ? { folder } : {},
  });
  return response.data as ArtworkBrowseResult;
}

export interface ArtworkRefreshResult {
  separations: InkSeparation[];
  /**
   * 'rejected' is refresh-only: the proposed path read nothing, so the backend
   * REFUSED to save it and the SKU is unchanged. It is not a warm outcome, so
   * it isn't part of WarmStatus.
   */
  status: WarmStatus | 'rejected';
  path: string | null;
  overridden: boolean;
  /**
   * True only when this call actually wrote an override — i.e. a proposed path
   * that read cleanly. Optional so a frontend running ahead of the backend
   * doesn't misread `undefined` as a failed save; prefer testing
   * `status === 'rejected'` for the failure case.
   */
  saved?: boolean;
  error?: string | null;
}

/**
 * Re-point and/or re-read ONE SKU's artwork. `path` saves an override for the
 * finished-good (so every job on it follows), `reset` drops back to the naming
 * convention, neither just re-reads the current path. The backend clears the
 * separation, thumbnail and negative caches first, so this always costs a real
 * Cloudflow call — human-initiated, one SKU at a time.
 *
 * NOT routed through serial() on purpose: an operator waiting on a fix should
 * not sit behind a few hundred queued background warms. Worst case this runs
 * alongside the single in-flight warm — two calls, not a burst.
 *
 * A proposed `path` is read BEFORE it is saved: if nothing readable is there the
 * result comes back status 'rejected' with the SKU untouched, rather than
 * pinning it to a dead path (an override suppresses every other candidate, so a
 * saved-but-wrong path cannot recover on its own).
 */
export async function refreshArtworkSeparations(payload: {
  customer: string; ref: string; path?: string; reset?: boolean; note?: string;
}): Promise<ArtworkRefreshResult> {
  const response = await api.post('/production/artwork-separations/refresh', payload);
  return response.data as ArtworkRefreshResult;
}

/** A path worth TRYING for a SKU the naming convention can't find. */
export interface ArtworkPathSuggestion {
  path: string;
  /** The customer code whose tree this path sits in (e.g. 'MPA' for a UBA item). */
  alias: string;
  /** The finished-good code under that alias (e.g. 'MPA5050048'). */
  customer_ref: string;
  /** Why this is worth trying — shown to the operator. */
  reason: string;
}

/**
 * Where else this SKU's artwork might be filed. Pure string work server-side (no
 * Cloudflow call), and empty for almost every SKU — only customers known to file
 * under another code's tree produce one. A suggestion is a starting point for a
 * human: picking it still has to survive the read-before-save check above.
 */
export async function getArtworkPathSuggestions(
  customer: string, ref: string,
): Promise<ArtworkPathSuggestion[]> {
  const response = await api.get('/production/artwork-path-suggestions', {
    params: { customer, ref },
  });
  return (response.data?.suggestions ?? []) as ArtworkPathSuggestion[];
}

// ----- Artwork health (migration 138) — the nightly warm's failure worklist ----

/** One item the nightly artwork warm keeps failing on. */
export interface ArtworkWarmFailure {
  customer_code: string;
  stock_code: string;
  /** CONSECUTIVE failed attempts — reset to 0 by any success. Ranks the list. */
  fail_count: number;
  /** timeout | transport | cloudflow | not_found | no_preview | no_path | config | error */
  last_error_kind: string | null;
  last_error: string | null;
  last_attempt_at: string;
  /** null = this item has NEVER warmed. Non-null = it used to work (artwork moved). */
  last_ok_at: string | null;
  /** True when someone already corrected the path and it still fails. */
  has_path_override: boolean;
  override_path: string | null;
  is_open_job: boolean;
  next_due: string | null;
  last_job_date: string | null;
  job_count: number;
  description: string | null;
}

export interface ArtworkWarmFailureKind {
  last_error_kind: string | null;
  items: number;
  with_override: number;
  open_jobs: number;
}

export interface ArtworkWarmHealth {
  failures: ArtworkWarmFailure[];
  returned: number;
  limit: number;
  summary: {
    total: number;
    with_override: number;
    open_jobs: number;
    by_kind: ArtworkWarmFailureKind[];
    /** Items the warm has ever attempted — the denominator for `total`. */
    tracked: number;
    ok: number;
    never_ok: number;
  };
  warm: {
    last_success_at: string | null;
    last_error_at: string | null;
    last_error_msg: string | null;
    error_count: number;
    total_syncs: number;
  } | null;
}

export interface ArtworkWarmFailureParams {
  kind?: string;
  search?: string;
  openOnly?: boolean;
  limit?: number;
}

export async function getArtworkWarmFailures(
  params: ArtworkWarmFailureParams = {},
): Promise<ArtworkWarmHealth> {
  const response = await api.get('/production/artwork-warm-failures', {
    params: {
      kind: params.kind || undefined,
      search: params.search || undefined,
      open_only: params.openOnly ? 'true' : undefined,
      limit: params.limit || undefined,
    },
  });
  return response.data as ArtworkWarmHealth;
}

// ----- Artwork spec check — Prism print_spec vs the artwork's separations -----
// The ink sticker's ECG-vs-Pantone rule (ink_spec_mismatch) applied to every
// open job, plus the same idea in the other directions. Verdicts come from
// backend helpers/spec_check.py — the ONE place the two strings are compared.

export type ArtworkSpecStatus = 'mismatch' | 'unverified' | 'ok' | 'no_artwork' | 'no_spec';

export interface ArtworkSpecIssue {
  /** ecg_has_pantone | ecg_has_spot | ecg_missing_colour | ecg_extra_colour |
   *  artwork_is_ecg | spec_pms_missing | artwork_pantone_not_in_spec |
   *  spec_spot_missing | artwork_spot_not_in_spec | process_mismatch */
  kind: string;
  detail: string;
}

export interface ArtworkSpecCheckJob {
  job_number: string;
  customer_code: string;
  customer_name: string | null;
  customer_ref: string;
  description: string | null;
  forme_number: string | null;
  print_spec: string | null;
  due_date: string | null;
  quantity: number | null;
  job_status: number | null;
  is_open_job: boolean;
  scheduled_press: string | null;
  scheduled_start: string | null;
  /** The path that resolved (or the first convention candidate when nothing did). */
  artwork_path: string | null;
  artwork_path_overridden: boolean;
  /** Only when status = no_artwork: not_found | not_warmed | no_path | a warm error kind. */
  artwork_reason: string | null;
  status: ArtworkSpecStatus;
  issues: ArtworkSpecIssue[];
  /** Free-text pairings we could not verify by name — shown, never counted as faults. */
  notes: string[];
  spec: {
    raw: string | null; is_ecg: boolean; has_content: boolean;
    /** b/c/m/y — B is black, never K. */
    process: string[]; process_unspecified: boolean;
    /** o/g/v */
    ecg_extras: string[]; pms: string[]; named_spots: string[]; ignored: string[];
  };
  artwork: {
    process: string[]; ecg_extras: string[];
    pantone: { code: string; name: string }[];
    spots: string[]; special: string[]; coatings: string[];
  };
  /** The non-technical separations compared, in file order. */
  separations: { name: string; kind: string }[];
}

export interface ArtworkSpecCheckKind { kind: string; label: string; jobs: number }

export interface ArtworkSpecCheck {
  jobs: ArtworkSpecCheckJob[];
  returned: number;
  /** Rows matching the filters (returned is capped by limit). */
  matched: number;
  limit: number;
  scope: 'open' | 'recent';
  /** Whole-scope counts, unaffected by status/kind/search — the chips. */
  summary: {
    total: number;
    open_jobs: number;
    by_status: Record<ArtworkSpecStatus, number>;
    by_kind: ArtworkSpecCheckKind[];
  };
  kinds: Record<string, string>;
}

export interface ArtworkSpecCheckParams {
  /** open (default) = job_status 3/NULL; recent = open + due in the last 60 days. */
  scope?: 'open' | 'recent';
  status?: string;
  kind?: string;
  search?: string;
  limit?: number;
}

export async function getArtworkSpecCheck(
  params: ArtworkSpecCheckParams = {},
): Promise<ArtworkSpecCheck> {
  const response = await api.get('/production/artwork-spec-check', {
    params: {
      scope: params.scope || undefined,
      status: params.status || undefined,
      kind: params.kind || undefined,
      search: params.search || undefined,
      limit: params.limit || undefined,
    },
  });
  return response.data as ArtworkSpecCheck;
}

// ----- Ink assignment (client-scoped colour -> sourced-ink library) -----

export interface InkAssignmentClient {
  client: string;
  skus: number;
  formes: number;
  cached?: number;
}

/** One candidate ink for an unassigned separation, and where it came from. */
export interface InkRecommendationOption {
  ink_key: string;
  description: string;
  series_suffix: string | null;
  series_hex: string | null;
  low_migration: boolean;
  source: 'category' | 'client' | 'catalogue';
  count: number | null;            // precedent: how many of the client's SKUs use it
}

/**
 * A suggested ink for a separation with no ink yet — with its reason. Nothing
 * is written until the operator accepts it. `pick` is set only when the answer
 * is unambiguous (one option, or a precedent used strictly more than any other);
 * otherwise the options are offered and the choice is the operator's. `draft`
 * pre-fills a new ink master when the register has nothing for the colour.
 */
export interface InkRecommendation {
  source: 'category' | 'client' | 'catalogue' | 'none';
  options: InkRecommendationOption[];
  pick?: string | null;
  reason?: string;
  draft?: { colour_code: string | null; colour_name: string; colour_kind: string; hex_colour: string | null };
}

export interface InkSeparation {
  name: string;
  kind: string;
  hex_colour: string | null;
  print_unit: number | null;
  ink_code: string | null;
  mileage_g_per_m2: number | null;   // per-separation ink film weight override; null = system default
  recommendation?: InkRecommendation | null;   // present only while ink_code is empty
}

/** The two coater slots for a SKU — each holds a coating ink_key or null. */
export interface InkSkuCoatings {
  '1': string | null;
  '2': string | null;
}

export interface InkAssignmentSku {
  sku: string;
  description: string | null;
  ink_spec: string | null;   // Prism IM_SPEC free-text (cross-check vs separations)
  active: boolean;
  /** Cloudflow path these separations were read from (override or convention). */
  artwork_path: string | null;
  /** True when artwork_path is a saved override, not the naming convention. */
  artwork_overridden: boolean;
  separations: InkSeparation[];
  coatings: InkSkuCoatings;
}

export interface InkAssignmentForme {
  forme: string;
  skus: InkAssignmentSku[];
}

export interface InkAssignmentData {
  client: string;
  summary: {
    skus: number;
    formes: number;
    separations: number;
    assigned: number;
  };
  formes: InkAssignmentForme[];
}

export interface InkSeparationUpsert {
  customer_code: string;
  sku: string;
  colour_name: string;
  kind?: string | null;
  hex_colour?: string | null;
  print_unit?: number | null;
  ink_code?: string | null;
  // Only send this key when actually editing the mileage — the backend leaves a
  // saved override untouched when it's absent, and clears it when null/blank.
  mileage_g_per_m2?: number | null;
}

export async function getInkAssignmentClients(): Promise<InkAssignmentClient[]> {
  const response = await api.get('/production/ink-clients');
  return response.data.clients;
}

export async function getInkAssignment(client: string, showInactive = false): Promise<InkAssignmentData> {
  const response = await api.get(`/production/ink-assignment/${encodeURIComponent(client)}`, {
    params: showInactive ? { show_inactive: 1 } : {},
  });
  return response.data;
}

export async function upsertInkSeparation(payload: InkSeparationUpsert): Promise<void> {
  await api.post('/production/ink-colour', payload);
}

// ----- Ink sticker (Job Bag scan-to-print bridge) -----

/** One press unit's ink on the sticker: colour + sourced series + demand kg. */
export interface InkStickerUnit {
  unit: number;
  name: string;
  kind: string | null;
  hex_colour: string | null;
  ink_key: string | null;
  ink_colour: string | null;   // the ink's own colour name (e.g. REMEDY GINGER LEMON) — matters for spots
  series: string | null;
  series_hex: string | null;
  low_migration: boolean;
  demand_kg: number | null;
  coverage_frac: number | null;      // 0..1 ink coverage on the 1UP — the demand chain's first factor
  mileage_g_per_m2: number | null;   // g/m² applied to this colour's demand
  mileage_overridden: boolean;       // true = tuned per-separation, not the house default
}

/** One coater slot on the sticker: coating product + 100%-coverage demand kg. */
export interface InkStickerCoating {
  slot: number;
  name: string;
  ink_key: string | null;
  hex_colour: string | null;
  demand_kg: number | null;
}

/** An ink separation printing on the job that hasn't been given a unit yet. */
export interface InkStickerUnassigned {
  name: string;
  kind: string | null;
  hex_colour: string | null;
  demand_kg: number | null;
  coverage_frac: number | null;
  mileage_g_per_m2: number | null;
  mileage_overridden: boolean;
}

/**
 * Temporary: one separation in the artwork's own file order, no unit tag —
 * assigned inks carry their resolved colour/series, unassigned ones fall
 * back to the raw separation name. Coatings are appended after the run.
 */
export interface InkStickerSeparation {
  name: string;
  kind: string | null;
  hex_colour: string | null;
  ink_key: string | null;
  ink_colour: string | null;
  series: string | null;
  series_hex: string | null;
  low_migration: boolean;
  demand_kg: number | null;
  coverage_frac: number | null;
  mileage_g_per_m2: number | null;
  mileage_overridden: boolean;
}

export interface InkStickerData {
  job_number: string;
  customer_code: string | null;
  customer_name: string | null;
  customer_ref: string | null;
  description: string | null;
  forme_number: string | null;
  stock_code: string | null;
  material_desc: string | null;
  run_length_sheets: number | null;
  quantity: number | null;
  /** Prism's own raw ink build string (e.g. "ECG B-ECG C-ECG M-ECG O-ECG Y") — a cross-check, not derived from the computed separations. */
  print_spec: string | null;
  sheet_width_mm: number | null;
  sheet_height_mm: number | null;
  sheet_area_m2: number | null;
  mileage_g_per_m2: number;
  artwork_status: 'no_path' | 'resolved' | 'not_found' | 'error';
  cloudflow_error: string | null;
  units: InkStickerUnit[];
  coatings: InkStickerCoating[];
  unassigned: InkStickerUnassigned[];
  separations_in_order: InkStickerSeparation[];
  /** The path actually used (override, or the first convention candidate tried) — reviewable/overridable regardless of artwork_status. */
  artwork_path: string | null;
  /** True when artwork_path came from production.artwork_path_overrides — a manual correction, not the naming convention. */
  artwork_path_overridden: boolean;
  total_demand_kg: number | null;
  /** True when print_spec says ECG but the resolved artwork still carries a Pantone/PMS separation — a strong signal the wrong file resolved. */
  ink_spec_mismatch: boolean;
  ink_spec_mismatch_detail: string | null;
  /** The full spec-vs-artwork verdict (same helper as Artwork health → Spec vs artwork). Only ecg_has_pantone holds auto-print. */
  spec_check?: { status: ArtworkSpecStatus; issues: ArtworkSpecIssue[]; notes: string[] };
  warnings: string[];
}

/**
 * Everything the B5 Job Bag ink sticker needs for one job in a single call:
 * header + per-unit ink layout (U1..U7, series + demand kg) + unassigned strip
 * + total kg. Backend joins the saved assignment to the demand calc. One job,
 * scan-triggered — pulls Cloudflow on demand (cache-first).
 */
export async function getInkSticker(
  jobNumber: string,
  mileage?: number,
): Promise<InkStickerData> {
  const response = await api.get(
    `/production/jobs/${encodeURIComponent(jobNumber)}/ink-sticker`,
    { params: { mileage: mileage ?? undefined } },
  );
  return response.data;
}

// ----- Artwork path overrides (migration 126) -----
// The exception list _artwork_pdf_candidates() checks ahead of the naming
// convention — one row per finished-good (customer_code + customer_ref), so
// fixing it once benefits every job on that SKU, not just the one raised.

export interface ArtworkPathOverride {
  customer_code: string;
  customer_ref: string;
  pdf_path: string;
  note: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export async function listArtworkPathOverrides(): Promise<ArtworkPathOverride[]> {
  const response = await api.get('/production/artwork-path-overrides');
  return response.data.overrides;
}

/** Upsert — a second call for the same customer_code+customer_ref replaces the path. */
export async function upsertArtworkPathOverride(payload: {
  customer_code: string; customer_ref: string; pdf_path: string;
  note?: string; created_by?: string;
}): Promise<ArtworkPathOverride> {
  const response = await api.post('/production/artwork-path-overrides', payload);
  return response.data;
}

/** Remove an override — resolution reverts to the naming convention. */
export async function deleteArtworkPathOverride(customerCode: string, customerRef: string): Promise<void> {
  await api.delete(
    `/production/artwork-path-overrides/${encodeURIComponent(customerCode)}/${encodeURIComponent(customerRef)}`,
  );
}

// ----- Ink catalogue: series master + product register (Materials CRUD) -----

/** A series row: manufacturer × product line × system (the key suffix). */
export interface InkSeries {
  series_suffix: string;   // e.g. 'HEAQ'
  manufacturer: string;    // HUBER
  series_name: string;     // ECO
  ink_system: string;      // AQ | UV
  display_hex: string | null;
  low_migration: boolean;  // LM is a SERIES property (baked into name + suffix)
  material_class: string;  // 'ink' (colour×series keys) | 'coating' (article-no keys)
  active: boolean;
  notes: string | null;
  /** Default supplier for every product in the series; products may override it. */
  supplier_code?: string | null;
  product_count?: number;
  created_at?: string;
  updated_at?: string;
}

export interface InkSeriesUpsert {
  series_suffix: string;
  manufacturer: string;
  series_name: string;
  ink_system: string;
  display_hex?: string | null;
  low_migration?: boolean;
  material_class?: string;
  active?: boolean;
  notes?: string | null;
}

/** A product row: colour × series (× LMQ) = the unique ink_key. */
export interface InkProduct {
  ink_key: string;         // CHEAQ
  material_class: string;  // 'ink'
  colour_code: string;     // C, P7680, SKC
  colour_name: string;     // Cyan, PMS 7680
  colour_kind: string | null;
  series_suffix: string;
  low_migration: boolean;
  description: string;     // HUBER CYAN ECO AQ
  hex_colour: string | null;
  active: boolean;
  notes: string | null;
  // Commercial fields (migration 109) — what ordering and reordering need.
  // supplier_code overrides the series' default; pack_size_kg is a PURCHASING
  // conversion only (stock is held in kg), so 20.0 means a 100kg order is 5 pails.
  supplier_code?: string | null;
  supplier_article_no?: string | null;   // THEIR code — what goes on the PO
  pack_size_kg?: number | null;
  standard_cost_per_kg?: number | null;
  reorder_point_kg?: number | null;
  // joined from the series
  manufacturer?: string;
  series_name?: string;
  ink_system?: string;
  series_hex?: string | null;
  updated_at?: string;
}

export interface InkProductUpsert {
  colour_code: string;
  colour_name: string;
  colour_kind?: string | null;
  series_suffix: string;   // LM is inherited from the series — not set per product
  hex_colour?: string | null;
  material_class?: string;
  active?: boolean;
  notes?: string | null;
  supplier_code?: string | null;
  supplier_article_no?: string | null;
  pack_size_kg?: number | null;
  standard_cost_per_kg?: number | null;
  reorder_point_kg?: number | null;
  /** Pass when an edit changed the identity fields so the old key is retired. */
  original_key?: string | null;
}

/** A real ink-colour hex harvested from artwork (production.ink_sku_separation). */
export interface InkColourSwatch {
  colour_name: string;
  hex_colour: string;
  uses: number;
}

export async function getInkColourSwatches(q?: string): Promise<InkColourSwatch[]> {
  const response = await api.get('/production/ink-colour-swatches', { params: { q: q || undefined } });
  return response.data.swatches;
}

/**
 * On-demand colour lookup across ALL extracted artwork (not just assigned
 * colours). Button-triggered only — never call this per keystroke. Returns real
 * Cloudflow separation hexes matching the colour-name query.
 */
export async function getArtworkColours(q: string): Promise<InkColourSwatch[]> {
  const response = await api.get('/production/artwork-colours', { params: { q } });
  return response.data.colours;
}

export async function listInkSeries(includeInactive = false): Promise<InkSeries[]> {
  const response = await api.get('/production/ink-series', {
    params: includeInactive ? { all: 1 } : {},
  });
  return response.data.series;
}

export async function upsertInkSeries(payload: InkSeriesUpsert): Promise<InkSeries> {
  const response = await api.post('/production/ink-series', payload);
  return response.data.series;
}

export async function deleteInkSeries(suffix: string): Promise<{ action: string }> {
  const response = await api.delete(`/production/ink-series/${encodeURIComponent(suffix)}`);
  return { action: response.data.action };
}

export interface InkProductListParams {
  includeInactive?: boolean;
  series?: string;
  search?: string;
  /** 'ink' (default on the backend), 'coating', or 'all'. */
  materialClass?: string;
}

export async function listInkProducts(params: InkProductListParams = {}): Promise<InkProduct[]> {
  const response = await api.get('/production/ink-products', {
    params: {
      all: params.includeInactive ? 1 : undefined,
      series: params.series || undefined,
      search: params.search || undefined,
      material_class: params.materialClass || undefined,
    },
  });
  return response.data.products;
}

/** The coatings register — ink_products with material_class='coating'. */
export async function listCoatings(includeInactive = false): Promise<InkProduct[]> {
  return listInkProducts({ materialClass: 'coating', includeInactive });
}

/**
 * Save one coater slot's coating for a SKU (slot 1 or 2). Pass ink_code=null to
 * clear the slot. Backend: production/ink-sku-coating (migration 106).
 */
export async function upsertInkSkuCoating(payload: {
  customer_code: string; sku: string; slot: 1 | 2; ink_code: string | null;
}): Promise<void> {
  await api.post('/production/ink-sku-coating', payload);
}

export async function upsertInkProduct(payload: InkProductUpsert): Promise<InkProduct> {
  const response = await api.post('/production/ink-products', payload);
  return response.data.product;
}

export async function deleteInkProduct(inkKey: string): Promise<{ action: string }> {
  const response = await api.delete(`/production/ink-products/${encodeURIComponent(inkKey)}`);
  return { action: response.data.action };
}

// ===========================================================================
// Ink stock — suppliers, the ledger, batches, trolley runs.
// Backend: blueprints/ink_stock.py. Data model: migrations 109 + 110.
//
// The one rule to keep in mind when reading these types: stock quantities are
// always POSITIVE kg. Direction lives in from_location/to_location, where null
// means "outside the business". So an `issue` to a trolley is a TRANSFER (the
// ink is still ours) and value only leaves on a `depletion`, which is booked
// when a trolley is reconciled.
// ===========================================================================

// ----- Suppliers -----

export interface InkSupplier {
  supplier_code: string;          // ALL CAPS: FLINT, HUBER
  name: string;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  account_ref: string | null;
  lead_time_days: number | null;
  active: boolean;
  notes: string | null;
  product_count?: number;
}

export interface InkSupplierUpsert {
  supplier_code: string;
  name: string;
  contact_name?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  account_ref?: string | null;
  lead_time_days?: number | null;
  active?: boolean;
  notes?: string | null;
}

export async function listInkSuppliers(includeInactive = false): Promise<InkSupplier[]> {
  const response = await api.get('/production/ink-suppliers', {
    params: includeInactive ? { all: 1 } : {},
  });
  return response.data.suppliers ?? [];
}

export async function upsertInkSupplier(payload: InkSupplierUpsert): Promise<InkSupplier> {
  const response = await api.post('/production/ink-suppliers', payload);
  return response.data.supplier;
}

export async function deleteInkSupplier(code: string): Promise<{ action: string }> {
  const response = await api.delete(`/production/ink-suppliers/${encodeURIComponent(code)}`);
  return { action: response.data.action };
}

// ----- Locations -----

export interface InkLocation {
  location_code: string;                    // STORE, TROLLEY-1
  name: string;
  location_type: 'store' | 'trolley';
  active: boolean;
  notes: string | null;
  /** Set when a trolley currently has an open run on it — a trolley holds at most one. */
  open_run_id: number | null;
}

export async function listInkLocations(): Promise<InkLocation[]> {
  const response = await api.get('/production/ink-locations');
  return response.data.locations ?? [];
}

// ----- Stock -----

/** One row per ink SKU: what we hold, where it is, what it's worth. */
export interface InkStockRow {
  ink_key: string;
  description: string;
  colour_name: string;
  colour_kind: string | null;
  hex_colour: string | null;
  material_class: string;
  active: boolean;
  series_suffix: string;
  series_name: string;
  manufacturer: string;
  ink_system: string;              // UV | AQ | CO — never sum across these
  low_migration: boolean;
  series_hex: string | null;
  supplier_code: string | null;    // resolved: product override, else series default
  supplier_article_no: string | null;
  pack_size_kg: number | null;
  standard_cost_per_kg: number | null;
  reorder_point_kg: number | null;
  store_kg: number;
  trolley_kg: number;              // out on trolleys — still ours, not yet consumed
  on_hand_kg: number;
  consigned_kg?: number;           // sealed on-hand owned by the supplier (until used)
  owned_kg?: number;               // sealed on-hand owned by us
  consigned_tubs?: number;
  owned_tubs?: number;
  stock_value: number;
  open_batches: number;
  below_reorder_point: boolean;
}

export interface InkStockParams {
  materialClass?: 'ink' | 'coating' | 'all';
  search?: string;
  belowReorder?: boolean;
  includeInactive?: boolean;
}

export interface InkStockReport {
  stock: InkStockRow[];
  total_stock_value: number;
  below_reorder_count: number;
}

export async function getInkStock(params: InkStockParams = {}): Promise<InkStockReport> {
  const response = await api.get('/production/ink-stock', {
    params: {
      material_class: params.materialClass ?? 'all',
      search: params.search || undefined,
      below_reorder: params.belowReorder ? 1 : undefined,
      all: params.includeInactive ? 1 : undefined,
    },
  });
  return {
    stock: response.data.stock ?? [],
    total_stock_value: response.data.total_stock_value ?? 0,
    below_reorder_count: response.data.below_reorder_count ?? 0,
  };
}

// ----- Batches -----

/** A batch = one delivery of one SKU. batch_no is OURS (on the label we print);
 *  supplier_batch_code is THEIRS. The pair is what makes a recall traceable. */
export interface InkBatch {
  batch_id: number;
  batch_no: string;
  ink_key: string;
  supplier_code: string | null;
  supplier_batch_code: string | null;
  po_number: string | null;
  received_at: string;
  expiry_date: string | null;
  qty_received_kg: number;
  unit_cost_per_kg: number | null;
  remaining_kg: number;
  remaining_value: number;
}

export async function listInkBatches(params: {
  inkKey?: string; supplierCode?: string; openOnly?: boolean;
} = {}): Promise<InkBatch[]> {
  const response = await api.get('/production/ink-batches', {
    params: {
      ink_key: params.inkKey || undefined,
      supplier_code: params.supplierCode || undefined,
      open: params.openOnly ? 1 : undefined,
    },
  });
  return response.data.batches ?? [];
}

// ----- Ledger (the audit trail) -----

export type InkTxnType = 'opening' | 'receipt' | 'issue' | 'return' | 'depletion' | 'adjustment';

export interface InkLedgerRow {
  id: number;
  ink_key: string;
  description: string | null;
  batch_id: number | null;
  batch_no: string | null;
  supplier_batch_code: string | null;
  txn_type: InkTxnType;
  qty_kg: number;                    // always positive
  from_location: string | null;      // null = came from outside the business
  to_location: string | null;        // null = LEFT the business (this is real usage)
  trolley_run_id: number | null;
  unit_cost_per_kg: number | null;
  value: number;
  reason: string | null;
  performed_by: string | null;
  performed_at: string;
  notes: string | null;
}

export async function getInkLedger(params: {
  inkKey?: string; txnType?: InkTxnType; trolleyRunId?: number; limit?: number;
} = {}): Promise<InkLedgerRow[]> {
  const response = await api.get('/production/ink-ledger', {
    params: {
      ink_key: params.inkKey || undefined,
      txn_type: params.txnType || undefined,
      trolley_run_id: params.trolleyRunId || undefined,
      limit: params.limit || undefined,
    },
  });
  return response.data.ledger ?? [];
}

/** One physical tub/pot on a SKU's detail view — a single barcode with its full
 *  provenance. The grain everything (on-hand, valuation, the T-account) sums up from. */
export interface InkTubRow {
  unit_code: string;                 // the barcode on the pail
  owner: 'consigned' | 'owned';
  status: 'sealed' | 'opened' | 'written_off';
  location_code: string | null;
  nominal_kg: number;
  opened_at: string | null;
  opened_by: string | null;
  batch_id: number | null;
  batch_no: string | null;
  supplier_batch_code: string | null;
  /** Set ONLY by a press scan against a job — an opened tub without one was
   *  opened unscanned, and its supplier batch is tied to the run, not a job. */
  job_number?: string | null;
  trolley_run_id?: number | null;
}

/** Full picture for one SKU: the summary row, its open batches, where it is, its history, and its pots. */
export interface InkStockDetail {
  ink: InkStockRow;
  batches: InkBatch[];
  locations: { location: string; location_type: string; on_hand_kg: number }[];
  ledger: InkLedgerRow[];
  tubs: InkTubRow[];
}

export async function getInkStockDetail(inkKey: string): Promise<InkStockDetail> {
  const response = await api.get(`/production/ink-stock/${encodeURIComponent(inkKey)}`);
  return {
    ink: response.data.ink,
    batches: response.data.batches ?? [],
    locations: response.data.locations ?? [],
    ledger: response.data.ledger ?? [],
    tubs: response.data.tubs ?? [],
  };
}

/**
 * Per-pail Zebra ZPL for one batch (batch_no-01 .. -0N). Returns the raw ZPL text
 * — the caller downloads it as a .zpl the LAN PC sends to the printer.
 */
export async function getBatchPailLabels(batchNo: string, pails?: number): Promise<string> {
  const response = await api.get(`/production/ink-batches/${encodeURIComponent(batchNo)}/pail-labels`, {
    params: pails ? { pails } : undefined,
    responseType: 'text',
    transformResponse: [(d) => d],   // keep it a raw string, don't JSON.parse
  });
  return response.data as string;
}

// ----- Inflows: receipt, opening balance, adjustment -----

export interface InkReceiptLine {
  ink_key: string;
  qty_kg: number;                        // total = pack_size_kg × pack_count (derived)
  pack_size_kg?: number | null;          // kg per tub, entered explicitly at goods-in
  unit_cost_per_kg?: number | null;
  supplier_batch_code?: string | null;   // THEIR code, captured at physical receipt
  pack_count?: number | null;            // number of packs/tubs received
  received_at?: string | null;           // receipt date (ISO) — lets historical goods-in be backdated
  expiry_date?: string | null;
  po_number?: string | null;
  po_line_id?: number | null;            // draws down this PO line + links the batch
  notes?: string | null;
}

export interface InkReceiptPayload {
  supplier_code?: string | null;
  po_number?: string | null;
  received_by?: string | null;
  items: InkReceiptLine[];
}

/** Goods-in. Creates a batch per line (each gets OUR batch_no) and books it into the store. */
export async function createInkReceipt(payload: InkReceiptPayload): Promise<InkBatch[]> {
  const response = await api.post('/production/ink-receipts', payload);
  return response.data.batches ?? [];
}

/** Initial inventory load — the opening stock take. Same shape as a receipt, typed
 *  'opening' so the ledger can always tell it apart from a real goods-in. */
export async function createInkOpeningBalance(payload: {
  performed_by?: string | null;
  items: InkReceiptLine[];
}): Promise<InkBatch[]> {
  const response = await api.post('/production/ink-opening', payload);
  return response.data.batches ?? [];
}

export interface InkAdjustmentPayload {
  ink_key: string;
  direction: 'gain' | 'loss';
  qty_kg: number;
  reason: string;                        // required — 'stock take', 'spoilage', ...
  location?: string;                     // defaults to STORE
  unit_cost_per_kg?: number | null;      // gains only: the cost layer to create
  performed_by?: string | null;
  notes?: string | null;
}

export async function createInkAdjustment(
  payload: InkAdjustmentPayload,
): Promise<{ direction: string; ink_key: string; qty_kg: number; ledger_rows: number }> {
  const response = await api.post('/production/ink-adjustments', payload);
  return response.data;
}

// ----- Purchase orders (Phase 5) -----

export type InkPoStatus = 'draft' | 'issued' | 'received' | 'cancelled';

/** A PO line. On detail it also carries the SKU metadata + received progress. */
export interface InkPoLine {
  id?: number;
  line_no?: number;
  ink_key: string;
  qty_ordered_kg: number;
  qty_received_kg?: number;
  outstanding_kg?: number;
  unit_cost_per_kg: number | null;
  notes: string | null;
  description?: string;
  hex_colour?: string | null;
  low_migration?: boolean;
  series_suffix?: string;
  series_name?: string;
  ink_system?: string;
}

export interface InkPurchaseOrder {
  id: number;
  po_number: string;                     // OURS, e.g. PO-000001
  supplier_code: string;
  status: InkPoStatus;
  order_date: string | null;
  expected_date: string | null;
  notes: string | null;
  created_by: string | null;
  issued_at: string | null;
  prism_pushed_at: string | null;
  created_at: string;
  updated_at: string;
  line_count: number;
  total_ordered_kg: number;
  outstanding_kg: number;
  total_value: number;
  lines?: InkPoLine[];                    // present on the detail endpoint only
}

export interface InkPoLineInput {
  ink_key: string;
  qty_ordered_kg: number | string;
  unit_cost_per_kg?: number | string | null;
  notes?: string | null;
}

export interface InkPoInput {
  supplier_code: string;
  order_date?: string | null;
  expected_date?: string | null;
  notes?: string | null;
  created_by?: string | null;
  issue?: boolean;                        // create straight into 'issued' (placed)
  lines: InkPoLineInput[];
}

export async function listInkPurchaseOrders(
  params: { supplier?: string; status?: InkPoStatus } = {},
): Promise<InkPurchaseOrder[]> {
  const response = await api.get('/production/ink-purchase-orders', {
    params: { supplier: params.supplier || undefined, status: params.status || undefined },
  });
  return response.data.orders ?? [];
}

export async function getInkPurchaseOrder(id: number): Promise<InkPurchaseOrder> {
  const response = await api.get(`/production/ink-purchase-orders/${id}`);
  return response.data.order;
}

export async function createInkPurchaseOrder(input: InkPoInput): Promise<InkPurchaseOrder> {
  const response = await api.post('/production/ink-purchase-orders', input);
  return response.data.order;
}

export async function updateInkPurchaseOrder(id: number, input: InkPoInput): Promise<InkPurchaseOrder> {
  const response = await api.put(`/production/ink-purchase-orders/${id}`, input);
  return response.data.order;
}

export async function issueInkPurchaseOrder(id: number): Promise<InkPurchaseOrder> {
  const response = await api.post(`/production/ink-purchase-orders/${id}/issue`, {});
  return response.data.order;
}

export async function cancelInkPurchaseOrder(id: number): Promise<InkPurchaseOrder> {
  const response = await api.post(`/production/ink-purchase-orders/${id}/cancel`, {});
  return response.data.order;
}

export interface ArtworkWarmStats {
  candidate_items: number;
  fetch_limit: number;
  seps_fetched: number; seps_cached: number; seps_failed: number;
  thumb_fetched: number; thumb_cached: number; thumb_failed: number;
  deferred: number;
  fetches_used: number;
}

/** Manually warm the artwork cache (seps + previews) for active category items.
 *  Bounded server-side (limit <= 120); the nightly 1am timer seeds the backlog. */
export async function warmArtworkCache(limit = 60): Promise<ArtworkWarmStats> {
  const response = await api.post('/production/artwork-cache/warm', {}, { params: { limit } });
  return response.data.stats;
}

/** On-order kg per SKU (outstanding on issued POs) — the balance report's On order. */
export async function getInkOnOrder(): Promise<Record<string, number>> {
  const response = await api.get('/production/ink-on-order');
  const out: Record<string, number> = {};
  (response.data.on_order ?? []).forEach((r: { ink_key: string; on_order_kg: number }) => {
    out[r.ink_key] = Number(r.on_order_kg) || 0;
  });
  return out;
}

// ----- Trolley runs (gang-run kitting) -----

export type InkTrolleyStatus = 'kitted' | 'at_press' | 'returned' | 'cancelled';

export interface InkTrolleyRun {
  id: number;
  trolley_code: string;
  status: InkTrolleyStatus;
  job_numbers: string[];       // the gang run — usage is booked against the RUN, not per job
  family_run_id?: number | null; // the scheduler's run this kit is for (null for a hand-typed list)
  press_code: string | null;
  tag_code: string | null;
  issued_by: string | null;
  issued_at: string;
  returned_by: string | null;
  returned_at: string | null;
  notes: string | null;
  // roll-ups (list endpoint)
  issued_kg?: number;
  returned_kg?: number;
  used_kg?: number;
  used_value?: number;
  // detail endpoint
  on_trolley?: InkTrolleyContent[];
  units?: InkTrolleyUnit[];
}

/** Sealed tubs on the trolley right now, per ink, with their batch breakdown. */
export interface InkTrolleyContent {
  ink_key: string;
  tubs: number;
  qty_kg: number;
  batches: { batch_no: string; supplier_batch_code: string | null; tubs: number; qty_kg: number }[];
}

/** One physical tub this run touched — kitted (all), opened (used) or returned sealed. */
export interface InkTrolleyUnit {
  unit_code: string;
  ink_key: string;
  owner: 'consigned' | 'owned';
  status: 'sealed' | 'opened' | 'written_off';
  nominal_kg: number;
  location_code: string;
  opened_at: string | null;
  opened_by: string | null;
  job_number?: string | null;   // set only by a press scan (see InkTubRow)
  batch_no: string | null;
  supplier_batch_code: string | null;
  description: string | null;
}

/** One physical tub — for the kit scan lookup (resolve a scanned barcode). */
export interface InkUnitRow {
  unit_code: string;
  ink_key: string;
  batch_no: string;
  status: 'sealed' | 'opened' | 'written_off';
  owner: 'consigned' | 'owned';
  location_code: string;
  nominal_kg: number;
}

export async function listInkUnits(
  params: { location?: string; status?: string; ink_key?: string } = {},
): Promise<InkUnitRow[]> {
  const response = await api.get('/production/ink-units', { params });
  return response.data.units ?? [];
}

export interface InkTrolleyKitPayload {
  trolley_code: string;
  job_numbers?: string[];
  family_run_id?: number | null;
  press_code?: string | null;
  tag_code?: string | null;
  issued_by?: string | null;
  notes?: string | null;
  // Kit by SCANNED tubs (unit_codes — the tubs loaded onto the trolley), or by
  // whole tubs / kg (no-scanner fallback; the backend picks oldest-batch-first).
  items: { ink_key: string; unit_codes?: string[]; tubs?: number; qty_kg?: number }[];
}

export async function listInkTrolleyRuns(
  status?: 'open' | InkTrolleyStatus,
  job?: string,
): Promise<InkTrolleyRun[]> {
  const response = await api.get('/production/ink-trolley-runs', {
    params: { ...(status ? { status } : {}), ...(job ? { job } : {}) },
  });
  return response.data.runs ?? [];
}

/** One tub opened at the press, as the consume endpoint reports it back. */
export interface InkOpenedTub {
  unit_code: string;
  ink_key: string;
  owner: 'consigned' | 'owned';
  nominal_kg: number;
  job_number: string | null;
  trolley_run_id: number | null;
  opened_at: string;
  /** Stamped from the SESSION backend-side, never typed on the press screen. */
  opened_by: string | null;
  batch_no: string | null;
  supplier_batch_code: string | null;
  description: string | null;
}

export interface InkConsumeResult {
  opened: InkOpenedTub[];
  tubs_opened: number;
  used_kg: number;
  consigned_accrual_kg: number;
  run: { id: number; status: InkTrolleyStatus; press_code: string | null } | null;
  warnings: string[];
}

/**
 * Open a tub at the press against a job — the batch-to-job link (roadmap
 * session 2). One-way: the tub becomes 'opened' (used in full). With a
 * trolley_run_id the run must be open; a tub kitted onto a different run is
 * refused (409), a tub with no run is adopted with a warning. The first scan
 * moves the run to at_press.
 */
export async function consumeInkUnits(payload: {
  unit_codes: string[];
  job_number?: string | null;
  trolley_run_id?: number | null;
  press_code?: string | null;
}): Promise<InkConsumeResult> {
  const response = await api.post('/production/ink-units/consume', payload);
  return {
    opened: response.data.opened ?? [],
    tubs_opened: response.data.tubs_opened ?? 0,
    used_kg: response.data.used_kg ?? 0,
    consigned_accrual_kg: response.data.consigned_accrual_kg ?? 0,
    run: response.data.run ?? null,
    warnings: response.data.warnings ?? [],
  };
}

export async function getInkTrolleyRun(runId: number): Promise<InkTrolleyRun> {
  const response = await api.get(`/production/ink-trolley-runs/${runId}`);
  return response.data.run;
}

/**
 * Kit a gang run: FIFO-draw each ink out of the store and onto the trolley.
 * A TRANSFER — nothing is consumed yet. Fails with 409 if the trolley already
 * has an open run, or if the store can't cover the draw.
 */
export async function kitInkTrolley(
  payload: InkTrolleyKitPayload,
): Promise<{
  run: InkTrolleyRun;
  kitted: { ink_key: string; tubs: number; qty_kg: number; unit_codes: string[] }[];
  warnings: string[];          // e.g. FIFO — kitted out of oldest-batch order (non-blocking)
}> {
  const response = await api.post('/production/ink-trolley-runs', payload);
  return {
    run: response.data.run,
    kitted: response.data.kitted ?? [],
    warnings: response.data.warnings ?? [],
  };
}

export async function setInkTrolleyRunStatus(
  runId: number, status: 'kitted' | 'at_press',
): Promise<InkTrolleyRun> {
  const response = await api.patch(`/production/ink-trolley-runs/${runId}`, { status });
  return response.data.run;
}

export interface InkReconcileResult {
  run_id: number;
  usage: {
    ink_key: string; issued_tubs: number; opened_tubs: number; returned_tubs: number; used_kg: number;
    press_scanned_tubs?: number;   // opened at the press against a job
    unscanned_tubs?: number;       // opened, but never scanned at the press
  }[];
  total_used_kg: number;
  total_used_value: number;
  /** The traceability gap: used tubs with no job scan, so their supplier batch
   *  is tied to the run only. Listed so settlement makes the gap visible. */
  unscanned?: { unit_code: string; ink_key: string; nominal_kg: number; supplier_batch_code: string | null }[];
  press_scanned_tubs?: number;
}

/**
 * The trolley comes back. Two ways to say what came back:
 *   SCAN  — `returned_unit_codes`: scan each SEALED tub as it goes back on the
 *           shelf. Every other kitted tub is what the run consumed. The floor
 *           workflow — scan what you shelve, no counting.
 *   COUNT — `returns: [{ink_key, opened_tubs}]`: the manual fallback (no scanner),
 *           how many tubs of each ink were opened (oldest-first).
 * An opened tub is used in full; opened consigned tubs are the Flint accrual.
 * Usage is booked against the RUN, not per job.
 */
export async function reconcileInkTrolleyRun(
  runId: number,
  payload: {
    returned_by?: string | null;
    mode?: 'scan' | 'count';
    returned_unit_codes?: string[];
    returns?: { ink_key: string; opened_tubs: number }[];
  },
): Promise<InkReconcileResult> {
  const response = await api.post(`/production/ink-trolley-runs/${runId}/reconcile`, payload);
  return response.data;
}

/** Kitted in error — put it all back, consume nothing. Distinct from reconciling
 *  with zero usage: no depletion row is written and nothing is charged to the run. */
export async function cancelInkTrolleyRun(
  runId: number, performedBy?: string | null,
): Promise<{ run_id: number; status: string }> {
  const response = await api.post(`/production/ink-trolley-runs/${runId}/cancel`, {
    performed_by: performedBy ?? null,
  });
  return response.data;
}

// ----- Ink demand forecast (migration 134) -----
// The whole demand tree in one read: five levels deep and aggregated client-side,
// because per-expansion fetching would both lag the interaction and make the
// additivity guarantee (a total that never moves when rows collapse) much harder
// to hold — parts of the tree would be from different reads. See
// backend/helpers/ink_forecast.py.

export interface InkForecastRun {
  id: number;
  projection_code: string;
  customer_code: string;
  projection_desc: string | null;
  source_file: string | null;
  month_labels: string[];
  imported_at: string;
  line_count: number;
  /** Lines carrying an actual requirement this horizon — a projection can hold
   *  hundreds of lines of which only a handful demand anything. */
  demand_line_count: number;
}

export async function listInkForecastRuns(): Promise<InkForecastRun[]> {
  const response = await api.get('/production/ink-forecast/runs');
  return response.data.runs;
}

/**
 * @param projections comma-separated "CODE:CUSTOMER" pairs — the Prism key is
 *   the PAIR, a code alone is ambiguous across customers. Omit for the newest
 *   projection of EVERY customer, which is the buyer's normal question: ink is
 *   many-to-many, so an order quantity is only right once every customer's
 *   demand for that ink is in the total.
 * @param customers comma-separated customer codes; newest projection for each.
 */
export async function getInkForecast(
  projections?: string, customers?: string,
): Promise<InkForecast> {
  const response = await api.get('/production/ink-forecast', {
    params: { projections: projections || undefined, customers: customers || undefined },
  });
  return response.data;
}

/** How well our parse of one PDF agreed with Prism's own printed roll-up. */
export interface InkForecastTie {
  cells: number;
  exact: number;
  /** null when Prism printed no comparable roll-up cells at all. */
  pct: number | null;
  clean: boolean;
  worst: {
    rm_code: string; month: string;
    ours: number; prism: number; diff: number;
  } | null;
}

export interface InkForecastImportResult {
  filename: string;
  ok: boolean;
  /** Present when ok — otherwise `error` is. */
  customer?: string;
  projection?: string;
  run_id?: number;
  /** True when this overwrote a projection already stored for the same
   *  (projection, customer). Worth showing: dropping a corrected PDF is a
   *  replace, and an operator should be told that rather than assume they added. */
  replaced?: boolean;
  lines?: number;
  lines_with_demand?: number;
  materials?: number;
  total_sheets?: number;
  months?: string[];
  tie?: InkForecastTie;
  error?: string;
}

export interface InkForecastImportResponse {
  success: boolean;
  imported: number;
  failed: number;
  results: InkForecastImportResult[];
}

/**
 * Upload Prism Raw Material forecast PDFs.
 *
 * Sent as ONE request with every file attached: the backend gives each file its
 * own transaction, so a bad PDF fails alone and the rest still land. The response
 * carries a verdict per file, including the roll-up tie — a file can parse
 * cleanly and still disagree with Prism, and that is imported and flagged rather
 * than rejected.
 */
export async function importInkForecasts(
  files: File[],
): Promise<InkForecastImportResponse> {
  const formData = new FormData();
  files.forEach((file, i) => formData.append(`file${i}`, file));
  const response = await api.post('/production/ink-forecast/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
}

// ----- Run readiness (Drum-Buffer-Rope) -----

/** One upstream stage pulled by the drum. `proxy` means the status is inferred
 *  from something adjacent rather than observed — the screen marks those. */
export interface ReadinessRope {
  stage: 'impose' | 'image' | 'ink' | 'board';
  ready_by: string | null;
  hours_left: number | null;
  status: 'ready' | 'blocked' | 'late' | 'due' | 'pending' | 'unknown';
  headline: string;
  detail: string | null;
  proxy: boolean;
}

export interface ReadinessRun {
  run_key: string;
  family_run_id: number | null;
  press_code: string;
  sequence: number;
  planned_start: string;
  status: string;
  passes: number;
  job_numbers: string[];
  customer_code: string | null;
  customer_ref: string | null;
  forme_number: string | null;
  description: string | null;
  total_sheets: number;
  stock_codes: string[];
  material_desc: string | null;
  ropes: ReadinessRope[];
  worst_status: ReadinessRope['status'];
}

export interface ReadinessReport {
  runs: ReadinessRun[];
  leads_hours: Record<string, number>;
  /** Always true today — the lead times are made up. See the blueprint header. */
  leads_placeholder: boolean;
  leads_overridden: boolean;
  due_window_hours: number;
}

/**
 * Every scheduled run with its four upstream ropes. A pure projection off
 * `schedule.planned_start` — reorder the board and every deadline moves.
 */
export async function getRunReadiness(
  params: { press?: string; leads?: Record<string, number> } = {},
): Promise<ReadinessReport> {
  const query: Record<string, string | number> = {};
  if (params.press) query.press = params.press;
  Object.entries(params.leads ?? {}).forEach(([k, v]) => { query[`${k}_lead`] = v; });
  const response = await api.get('/production/run-readiness', { params: query });
  return response.data;
}
